import { AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { ago, dt } from "./format";
import { C, CountUp, Dot, LogoPlate, Pill, Ring, cx } from "./ui";

function Kpi({
  label,
  value,
  sub,
  tone,
  i,
}: {
  label: string;
  value: number;
  sub?: string;
  tone?: "warn" | "bad";
  i: number;
}) {
  return (
    <div className="pf-card" style={{ ["--i" as string]: i }}>
      <div className="pf-kpi-label">{label}</div>
      <div className={cx("pf-kpi-value", tone && `is-${tone}`)}>
        <CountUp value={value} />
      </div>
      <div className="pf-kpi-sub">{sub ?? " "}</div>
    </div>
  );
}

export function Overview({ onOpenOrg }: { onOpenOrg: (id: string) => void }) {
  const q = trpc.platform.overview.useQuery(undefined, { refetchInterval: 60_000 });
  const orgs = trpc.platform.orgs.list.useQuery(undefined, { refetchInterval: 120_000 });

  if (q.isLoading) return <p className="pf-muted">Lädt …</p>;
  if (q.isError || !q.data)
    return (
      <div className="pf-card">
        Die Übersicht konnte nicht geladen werden.{" "}
        <button className="pf-btn sm" onClick={() => q.refetch()}>
          Erneut versuchen
        </button>
      </div>
    );

  const { system, totals, topErrors, failedJobs, inactive, activity14 } = q.data;
  const max = Math.max(1, ...activity14.map((a) => a.count));
  const Icon = system.status === "ok" ? CheckCircle2 : system.status === "warn" ? AlertTriangle : ShieldAlert;
  const statusColor = system.status === "ok" ? C.green : system.status === "warn" ? C.amber : C.red;
  const diskTone = system.disk.usedPct >= 85 ? "bad" : system.disk.usedPct >= 75 ? "warn" : undefined;
  const dbText =
    system.dbMb == null ? "—" : system.dbMb < 1024 ? `${system.dbMb} MB` : `${(system.dbMb / 1024).toFixed(1).replace(".", ",")} GB`;

  return (
    <div className="pf-grid" style={{ gap: 22 }}>
      <div className="pf-row" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <h2 className="pf-title">Plattform-Zentrale</h2>
          <p className="pf-lead">Alle Betriebe, Server und Störungen auf einen Blick.</p>
        </div>
        <div style={{ flex: 1 }} />
        <span className="pf-muted pf-mono" style={{ fontSize: 11 }}>
          Stand {dt(system.ts)}
        </span>
      </div>

      <div className={cx("pf-status", system.status === "ok" ? "" : system.status)}>
        <Icon size={26} color={statusColor} />
        <div>
          <b>
            {system.status === "ok"
              ? "Alle Systeme laufen"
              : system.status === "warn"
                ? "Achtung — bitte beobachten"
                : "Störung — bitte handeln"}
          </b>
          {system.problems.length ? (
            <ul>
              {system.problems.map((p, i) => (
                <li key={i}>{p.text}</li>
              ))}
            </ul>
          ) : (
            <div className="pf-muted" style={{ marginTop: 2 }}>
              Server, Datenbank und Sicherung arbeiten wie erwartet.
            </div>
          )}
        </div>
      </div>

      <div className="pf-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }}>
        <Kpi i={1} label="Betriebe" value={totals.orgs} sub={totals.suspended ? `${totals.suspended} gesperrt` : "alle aktiv"} />
        <Kpi i={2} label="Benutzer" value={totals.users} sub="aktive Konten" />
        <Kpi i={3} label="Artikel gesamt" value={totals.articles} sub="in allen Katalogen" />
        <Kpi
          i={4}
          label="Fehler · 24 Std."
          value={totals.errors24}
          sub={totals.errors24 ? "siehe Störungen" : "keine Auffälligkeiten"}
          tone={totals.errors24 > 20 ? "bad" : totals.errors24 > 0 ? "warn" : undefined}
        />
      </div>

      <section>
        <h3 className="pf-h">Deine Kunden</h3>
        <div className="pf-grid pf-tiles">
          {(orgs.data ?? []).map((o, i) => (
            <div
              key={o.id}
              className="pf-card is-link pf-tile"
              style={{ ["--i" as string]: i + 4 }}
              onClick={() => onOpenOrg(o.id)}
            >
              <div className="pf-tile-top">
                <LogoPlate url={o.logoUrl} name={o.legalName} size={56} wide />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="pf-tile-name">{o.legalName}</div>
                  <div className="pf-row" style={{ gap: 8, marginTop: 6 }}>
                    {o.suspendedAt ? (
                      <Pill color={C.red}>Gesperrt</Pill>
                    ) : (
                      <Pill color={C.green} dot>
                        Aktiv
                      </Pill>
                    )}
                    {o.errors7 > 0 && <Pill color={C.amber}>{o.errors7} Fehler</Pill>}
                  </div>
                </div>
              </div>
              <div className="pf-tile-stats">
                <div className="pf-stat">
                  <b>{o.users}</b>
                  <span>Benutzer</span>
                </div>
                <div className="pf-stat">
                  <b>{o.customers}</b>
                  <span>Kunden</span>
                </div>
                <div className="pf-stat">
                  <b>{o.invoices}</b>
                  <span>Rechnungen</span>
                </div>
              </div>
              <div className="pf-tile-foot">
                <span>Anmeldung {ago(o.lastLoginAt)}</span>
                <span>Aktivität {ago(o.lastActivityAt)}</span>
              </div>
            </div>
          ))}
          {orgs.isLoading && <div className="pf-card pf-muted">Lädt …</div>}
        </div>
      </section>

      <section>
        <h3 className="pf-h">Server</h3>
        <div className="pf-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          <div className="pf-card pf-row" style={{ gap: 18, ["--i" as string]: 1 }}>
            <Ring pct={system.disk.usedPct} tone={diskTone} label={`${Math.round(system.disk.usedPct)}%`} />
            <div>
              <div className="pf-kpi-label">Speicherplatz</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>{system.disk.freeGb} GB frei</div>
              <div className="pf-kpi-sub">von {system.disk.totalGb} GB</div>
            </div>
          </div>
          <div className="pf-card" style={{ ["--i" as string]: 2 }}>
            <div className="pf-kpi-label">Datenbank</div>
            <div className="pf-kpi-value" style={{ fontSize: 28 }}>
              {dbText}
            </div>
            <div className="pf-kpi-sub">alle Betriebe zusammen</div>
          </div>
          <div className="pf-card" style={{ ["--i" as string]: 3 }}>
            <div className="pf-kpi-label">Letzte Sicherung</div>
            <div
              className={cx("pf-kpi-value", (system.backup.ageHours == null || system.backup.ageHours > 30) && "is-bad")}
              style={{ fontSize: 28 }}
            >
              {system.backup.lastSuccessAt ? ago(system.backup.lastSuccessAt) : "keine"}
            </div>
            <div className="pf-kpi-sub">
              {system.backup.sizeMb ? `${system.backup.sizeMb} MB · extern ${system.backup.r2Ok ? "gesichert" : system.backup.r2Ok === false ? "FEHLER" : "—"}` : " "}
            </div>
          </div>
          <div className="pf-card" style={{ ["--i" as string]: 4 }}>
            <div className="pf-kpi-label">Aktivität · 14 Tage</div>
            <div className="pf-bars" style={{ marginTop: 14 }}>
              {activity14.length ? (
                activity14.map((a) => (
                  <i
                    key={String(a.day)}
                    title={`${new Date(a.day).toLocaleDateString("de-AT")}: ${a.count}`}
                    style={{ height: `${Math.max(6, (a.count / max) * 100)}%` }}
                  />
                ))
              ) : (
                <span className="pf-muted">keine Daten</span>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="pf-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))" }}>
        <section className="pf-card">
          <h3 className="pf-h">Häufigste Fehler · 24 Std.</h3>
          {topErrors.length ? (
            <ul className="pf-list">
              {topErrors.map((e, i) => (
                <li key={i}>
                  <div className="pf-row" style={{ gap: 8 }}>
                    <Pill color={C.red}>{e.count}×</Pill>
                    <code>{e.path}</code>
                  </div>
                  <div style={{ marginTop: 4 }}>{e.message}</div>
                  <div className="pf-muted" style={{ fontSize: 11.5 }}>
                    zuletzt {ago(e.lastAt)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="pf-row pf-muted">
              <Dot /> Keine Fehler in den letzten 24 Stunden.
            </div>
          )}
        </section>

        <section className="pf-card">
          <h3 className="pf-h">Katalog-Updates · fehlgeschlagen</h3>
          {failedJobs.length ? (
            <ul className="pf-list">
              {failedJobs.map((j) => (
                <li key={j.id}>
                  <b>{j.org}</b> <span className="pf-muted">· {dt(j.at)}</span>
                  <div style={{ color: C.red, marginTop: 2 }}>{j.error}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="pf-row pf-muted">
              <Dot /> Keine fehlgeschlagenen Updates in den letzten 7 Tagen.
            </div>
          )}
        </section>

        <section className="pf-card">
          <h3 className="pf-h">Ohne Anmeldung · 14 Tage</h3>
          {inactive.length ? (
            <div className="pf-row" style={{ flexWrap: "wrap", gap: 8 }}>
              {inactive.map((o) => (
                <button key={o.id} className="pf-btn sm" onClick={() => onOpenOrg(o.id)}>
                  {o.name} <span className="pf-muted">{ago(o.lastLoginAt)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="pf-row pf-muted">
              <Dot /> Alle Betriebe waren in den letzten 14 Tagen aktiv.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
